import React, { useCallback, useMemo, useState } from 'react';
import './add-exam-modal.scss';
import { Modal } from '@/components';
import DialogModal from '@/components/modal/DialogModal';
import Dropdown from '@/components/dropdown';
import { CustomDatePicker } from '@/components/datepicker/DatePicker';
import { useSizes } from '@/utility/responsive';

interface ExamRow {
    id: number;
    subject: string;
    correct: string;
    wrong: string;
    empty: string;
}

interface AddExamModalProps {
    isOpen: boolean;
    onClose: () => void;
}

const EXAM_TYPE_OPTIONS = [
    { id: 'TYT', name: 'TYT' },
    { id: 'AYT', name: 'AYT' },
    { id: 'YDT', name: 'YDT' },
    { id: 'LGS', name: 'LGS' },
    { id: 'BRANCH', name: 'Branş' },
];

const createEmptyRow = (id: number): ExamRow => ({
    id,
    subject: '',
    correct: '',
    wrong: '',
    empty: '',
});

const INITIAL_ROWS = [createEmptyRow(1), createEmptyRow(2), createEmptyRow(3), createEmptyRow(4)];

const toNum = (v: string) => parseFloat(v) || 0;

const calcNet = (correct: string, wrong: string): string => {
    const c = toNum(correct);
    const w = toNum(wrong);
    if (c === 0 && w === 0) return '-';
    const net = c - w / 4;
    return net % 1 === 0 ? String(net) : net.toFixed(2);
};

const AddExamModal: React.FC<AddExamModalProps> = ({ isOpen, onClose }) => {
    const { isMobile } = useSizes();
    const [examType, setExamType] = useState(EXAM_TYPE_OPTIONS[0]);
    const [examDate, setExamDate] = useState<Date | null>(null);
    const [rows, setRows] = useState<ExamRow[]>(INITIAL_ROWS);
    const [nextId, setNextId] = useState(5);
    const [showCloseConfirm, setShowCloseConfirm] = useState(false);

    const hasData = useMemo(() => {
        if (examDate) return true;
        return rows.some(r => r.subject || r.correct || r.wrong || r.empty);
    }, [examDate, rows]);

    const handleAddRow = useCallback(() => {
        setRows(prev => [...prev, createEmptyRow(nextId)]);
        setNextId(prev => prev + 1);
    }, [nextId]);

    const handleRowChange = useCallback((id: number, field: keyof ExamRow, value: string) => {
        setRows(prev =>
            prev.map(row => (row.id === id ? { ...row, [field]: value } : row))
        );
    }, []);

    const totals = useMemo(() => {
        const correct = rows.reduce((s, r) => s + toNum(r.correct), 0);
        const wrong = rows.reduce((s, r) => s + toNum(r.wrong), 0);
        const empty = rows.reduce((s, r) => s + toNum(r.empty), 0);
        const net = correct - wrong / 4;
        return {
            correct: correct || '-',
            wrong: wrong || '-',
            empty: empty || '-',
            net: correct === 0 && wrong === 0 ? '-' : net % 1 === 0 ? String(net) : net.toFixed(2),
        };
    }, [rows]);

    const resetAndClose = () => {
        setExamType(EXAM_TYPE_OPTIONS[0]);
        setExamDate(null);
        setRows(INITIAL_ROWS);
        setNextId(5);
        setShowCloseConfirm(false);
        onClose();
    };

    const handleCloseAttempt = () => {
        if (hasData) {
            setShowCloseConfirm(true);
        } else {
            resetAndClose();
        }
    };

    return (
        <>
            <Modal
                isOpen={isOpen}
                onClose={handleCloseAttempt}
                size={isMobile ? 'full' : 'lg'}
                closeOnClickOutside
                closeOnEsc
                showCloseButton
                bodyClassName="add-exam-modal"
            >
                <div className="add-exam-modal__info">
                    <h3 className="add-exam-modal__title">Deneme Sınavı Ekle</h3>
                    <div className="add-exam-modal__desc">
                        Öğrencinizin Doping Hafıza haricinde yapmış olduğu deneme sınavlarına ait gerekli bilgileri ekleyin.
                    </div>
                </div>

                <div className="add-exam-modal__selectors">
                    <div className="add-exam-modal__selector-group">
                        <label className="add-exam-modal__label">Deneme Türü Seçimi</label>
                        <Dropdown
                            placeholder="Deneme türünü seçin"
                            options={EXAM_TYPE_OPTIONS}
                            selectedOption={examType}
                            onOptionSelect={setExamType}
                            size="large"
                            fullWidth
                            className="add-exam-modal__dropdown"
                        />
                    </div>
                    <div className="add-exam-modal__selector-group">
                        <label className="add-exam-modal__label">Tarih Seçimi</label>
                        <CustomDatePicker
                            placeholderLabel="Tarih seçin"
                            selectedDate={examDate}
                            onChange={setExamDate}
                            dateFormat="dd.MM.yyyy"
                            className="add-exam-modal__datepicker"
                        />
                    </div>
                </div>

                <div className="add-exam-modal__table-wrap">
                    <table className="add-exam-modal__table">
                        <thead>
                            <tr>
                                <th className="add-exam-modal__th add-exam-modal__th--subject">Dersler</th>
                                <th className="add-exam-modal__th">Doğru</th>
                                <th className="add-exam-modal__th">Yanlış</th>
                                <th className="add-exam-modal__th">Boş</th>
                                <th className="add-exam-modal__th">Net</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map(row => (
                                <tr key={row.id} className="add-exam-modal__tr">
                                    <td className="add-exam-modal__td add-exam-modal__td--subject">
                                        <input
                                            className="add-exam-modal__cell-input add-exam-modal__cell-input--text"
                                            type="text"
                                            placeholder="Ders adı"
                                            value={row.subject}
                                            onChange={e => handleRowChange(row.id, 'subject', e.target.value)}
                                        />
                                    </td>
                                    <td className="add-exam-modal__td">
                                        <input
                                            className="add-exam-modal__cell-input"
                                            type="text"
                                            inputMode="numeric"
                                            placeholder="0"
                                            value={row.correct}
                                            onChange={e => handleRowChange(row.id, 'correct', e.target.value.replace(/[^0-9]/g, '').slice(0, 3))}
                                        />
                                    </td>
                                    <td className="add-exam-modal__td">
                                        <input
                                            className="add-exam-modal__cell-input"
                                            type="text"
                                            inputMode="numeric"
                                            placeholder="0"
                                            value={row.wrong}
                                            onChange={e => handleRowChange(row.id, 'wrong', e.target.value.replace(/[^0-9]/g, '').slice(0, 3))}
                                        />
                                    </td>
                                    <td className="add-exam-modal__td">
                                        <input
                                            className="add-exam-modal__cell-input"
                                            type="text"
                                            inputMode="numeric"
                                            placeholder="0"
                                            value={row.empty}
                                            onChange={e => handleRowChange(row.id, 'empty', e.target.value.replace(/[^0-9]/g, '').slice(0, 3))}
                                        />
                                    </td>
                                    <td className="add-exam-modal__td add-exam-modal__td--net">
                                        <span className="add-exam-modal__net">
                                            {calcNet(row.correct, row.wrong)}
                                        </span>
                                    </td>
                                </tr>
                            ))}

                            <tr className="add-exam-modal__tr add-exam-modal__tr--total">
                                <td className="add-exam-modal__td add-exam-modal__td--subject">
                                    <span className="add-exam-modal__total-label">Toplam</span>
                                </td>
                                <td className="add-exam-modal__td">
                                    <span className="add-exam-modal__total-value">{totals.correct}</span>
                                </td>
                                <td className="add-exam-modal__td">
                                    <span className="add-exam-modal__total-value">{totals.wrong}</span>
                                </td>
                                <td className="add-exam-modal__td">
                                    <span className="add-exam-modal__total-value">{totals.empty}</span>
                                </td>
                                <td className="add-exam-modal__td add-exam-modal__td--net">
                                    <span className="add-exam-modal__total-value">{totals.net}</span>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div className="add-exam-modal__actions">
                    <button className="add-exam-modal__add-row-btn" type="button" onClick={handleAddRow}>
                        <i className="icons-add-circle" />
                        <span>Ders Ekle</span>
                    </button>
                </div>

                <div className="add-exam-modal__footer">
                    <button className="add-exam-modal__save-btn" type="button">
                        Kaydet
                    </button>
                </div>
            </Modal>

            <DialogModal
                isOpen={showCloseConfirm}
                onClose={() => setShowCloseConfirm(false)}
                variant="warning"
                title="Bilgileri Kaydetmediniz!"
                secondaryButtonText="Evet"
                onSecondaryClick={resetAndClose}
                primaryButtonText="Vazgeç"
                onPrimaryClick={() => setShowCloseConfirm(false)}
                shouldCloseOnOverlayClick={false}
            >
                <p>Girmiş olduğunuz bilgileri kaydetmediniz. Düzenlemeye geri dönmek ister misiniz?</p>
            </DialogModal>
        </>
    );
};

export default AddExamModal;
